import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDiagnosticTests, useDiagnosticTest, useUpdateDiagnosticTest, useBulkDeleteDiagnosticTests, useBulkUpdateDiagnosticTests } from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { DiagnosticSubmitModal } from '../../components/ui/DiagnosticSubmitModal';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { FlaskConical, Loader2, AlertCircle, Plus, Pencil, Send, Search, X, Power, PowerOff, Trash2, Square, SquareCheck, Edit3, CheckCircle, XCircle, Tag, DollarSign, Clock, MapPin, List, Droplet } from 'lucide-react';
import { isBloodTest, testSpecimenTypeCode, testSpecimenTypeName, testContainerLabel, requiresSpecimen, requiredVolumeForTest } from '../../utils/specimen';
import { toast } from 'sonner';

type SpecimenFilter = 'all' | 'blood' | 'non_blood' | 'no_specimen';

export function DiagnosticTests() {
  const navigate = useNavigate();
  const location = useLocation();
  const limit = 20;

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [page, setPage] = useState(() => {
    const p = params.get('page');
    return p ? Math.max(1, parseInt(p, 10)) : 1;
  });
  const [search, setSearch] = useState(() => params.get('search') || '');
  const debouncedSearch = useDebounce(search, 350);
  const [statusFilter, setStatusFilter] = useState<'all' | 'ACTIVE' | 'INACTIVE'>('all');
  const [specimenFilter, setSpecimenFilter] = useState<SpecimenFilter>('all');
  const [sortBy, setSortBy] = useState(() => params.get('sortBy') || 'createdAt');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(() =>
    (params.get('sortOrder') || '').toUpperCase() === 'ASC' ? 'ASC' : 'DESC',
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [submitModal, setSubmitModal] = useState<{ open: boolean; id: string; name: string } | null>(null);
  const [viewModal, setViewModal] = useState<{ open: boolean; id: string } | null>(null);

  const { data, isLoading, error } = useDiagnosticTests(page, limit, debouncedSearch || undefined, sortBy, sortOrder);
  const updateMutation = useUpdateDiagnosticTest();
  const bulkDeleteMutation = useBulkDeleteDiagnosticTests();
  const bulkUpdateMutation = useBulkUpdateDiagnosticTests();
  const { data: viewData, isLoading: viewLoading, error: viewError } = useDiagnosticTest(
    viewModal?.open ? viewModal.id : undefined
  );
  const [togglingStatus, setTogglingStatus] = useState(false);

  const tests = data?.data || [];
  const meta = data?.meta;
  const trashedCount = Number(data?.stats?.trashed ?? 0);

  const filteredTests = useMemo(() => {
    let items = tests;
    if (statusFilter !== 'all') {
      items = items.filter((test: any) => test.status === statusFilter);
    }
    if (specimenFilter !== 'all') {
      items = items.filter((test: any) => {
        if (specimenFilter === 'blood') return isBloodTest(test);
        if (specimenFilter === 'non_blood') return !isBloodTest(test) && requiresSpecimen(test);
        if (specimenFilter === 'no_specimen') return !requiresSpecimen(test);
        return true;
      });
    }
    return items;
  }, [tests, statusFilter, specimenFilter]);

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (page > 1) newParams.set('page', String(page));
    if (debouncedSearch) newParams.set('search', debouncedSearch);
    if (sortBy !== 'createdAt' || sortOrder !== 'DESC') {
      newParams.set('sortBy', sortBy);
      newParams.set('sortOrder', sortOrder);
    }
    const query = newParams.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, { replace: true });
  }, [page, debouncedSearch, sortBy, sortOrder, navigate, location.pathname]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const allSelected = filteredTests.length > 0 && filteredTests.every((test: any) => selectedIds.includes(test.id));
  const someSelected = selectedIds.length > 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredTests.some((test: any) => test.id === id)));
    } else {
      const newIds = filteredTests.map((test: any) => test.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...newIds])));
    }
  };

  const toggleSelectOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    try {
      await bulkDeleteMutation.mutateAsync(ids);
      setSelectedIds([]);
      setBulkConfirmOpen(false);
      toast.success(`Deleted ${ids.length} test${ids.length === 1 ? '' : 's'}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to delete tests');
    }
  };

  const handleBulkActivate = async () => {
    const ids = [...selectedIds];
    try {
      await bulkUpdateMutation.mutateAsync({ ids, data: { status: 'ACTIVE' } });
      setSelectedIds([]);
      toast.success(`Activated ${ids.length} test${ids.length === 1 ? '' : 's'}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to activate tests');
    }
  };

  const handleBulkDeactivate = async () => {
    const ids = [...selectedIds];
    try {
      await bulkUpdateMutation.mutateAsync({ ids, data: { status: 'INACTIVE' } });
      setSelectedIds([]);
      toast.success(`Deactivated ${ids.length} test${ids.length === 1 ? '' : 's'}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to deactivate tests');
    }
  };

  const handleToggleStatus = async () => {
    if (!viewModal?.id || !viewData?.data) return;
    setTogglingStatus(true);
    try {
      await updateMutation.mutateAsync({
        id: viewModal.id,
        data: { status: viewData.data.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' },
      });
    } finally {
      setTogglingStatus(false);
    }
  };

  const test = viewData?.data;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
        <AlertCircle size={18} />
        <span>Failed to load diagnostic tests</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FlaskConical className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-text">Diagnostic Tests</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/administration/diagnostic-tests/trash')}
            className="px-4 py-2 border border-border text-text-muted hover:text-danger hover:border-danger rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
            title="View trash"
          >
            <Trash2 size={16} />
            Trash
            {trashedCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-semibold leading-none">
                {trashedCount > 99 ? '99+' : trashedCount}
              </span>
            )}
          </button>
          <button
            onClick={() => navigate('/administration/diagnostic-tests/new')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
          >
            <Plus size={16} />
            Add Test
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search tests..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as any); setSelectedIds([]); }}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
          <select
            value={specimenFilter}
            onChange={(e) => { setSpecimenFilter(e.target.value as SpecimenFilter); setSelectedIds([]); }}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="all">All Sample Types</option>
            <option value="blood">Blood (Blood/Serum/Plasma)</option>
            <option value="non_blood">Other Sample (Urine/Stool/Swab…)</option>
            <option value="no_specimen">No Sample (Procedure)</option>
          </select>
          <select
            value={`${sortBy}:${sortOrder}`}
            onChange={(e) => {
              const [nextBy, nextOrder] = e.target.value.split(':') as [string, 'ASC' | 'DESC'];
              setSortBy(nextBy);
              setSortOrder(nextOrder);
              setPage(1);
              setSelectedIds([]);
            }}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
            title="Sort tests"
          >
            <option value="createdAt:DESC">Newest first</option>
            <option value="createdAt:ASC">Oldest first</option>
            <option value="name:ASC">Name (A–Z)</option>
            <option value="name:DESC">Name (Z–A)</option>
            <option value="price:ASC">Price (low → high)</option>
            <option value="price:DESC">Price (high → low)</option>
            <option value="code:ASC">Code (A–Z)</option>
          </select>
          {(search || statusFilter !== 'all' || specimenFilter !== 'all') && (
            <button
              onClick={() => { setSearch(''); setStatusFilter('all'); setSpecimenFilter('all'); setPage(1); setSelectedIds([]); }}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {someSelected && (
        <div className="flex items-center gap-2 bg-surface border border-border rounded-xl p-3">
          <span className="text-sm text-text-muted mr-2">{selectedIds.length} selected</span>
          <button
            onClick={handleBulkActivate}
            disabled={bulkUpdateMutation.isPending}
            className="px-3 py-1.5 rounded-lg bg-success text-white text-xs font-medium hover:bg-success/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Power size={12} /> Activate
          </button>
          <button
            onClick={handleBulkDeactivate}
            disabled={bulkUpdateMutation.isPending}
            className="px-3 py-1.5 rounded-lg bg-warning text-white text-xs font-medium hover:bg-warning/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            <PowerOff size={12} /> Deactivate
          </button>
          <button
            onClick={() => setBulkConfirmOpen(true)}
            disabled={bulkDeleteMutation.isPending}
            className="px-3 py-1.5 rounded-lg bg-danger text-white text-xs font-medium hover:bg-danger/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="px-4 py-3 text-left w-10">
                  <button onClick={toggleSelectAll} className="text-text-muted hover:text-text transition-colors">
                    {allSelected ? <SquareCheck size={18} className="text-primary" /> : <Square size={18} />}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Sample Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Discount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Room</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredTests.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-text-muted">
                    {search || statusFilter !== 'all' || specimenFilter !== 'all' ? (
                      <span>No tests match your filters.{' '}
                        <button onClick={() => { setSearch(''); setStatusFilter('all'); setSpecimenFilter('all'); setPage(1); setSelectedIds([]); }} className="text-primary hover:underline">
                          Clear filters
                        </button>
                      </span>
                    ) : (
                      'No diagnostic tests found'
                    )}
                  </td>
                </tr>
              ) : (
                filteredTests.map((test: any) => (
                  <tr key={test.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <button onClick={() => toggleSelectOne(test.id)} className="text-text-muted hover:text-text transition-colors">
                        {selectedIds.includes(test.id) ? <SquareCheck size={18} className="text-primary" /> : <Square size={18} />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-text">
                      <button
                        onClick={() => setViewModal({ open: true, id: test.id })}
                        className="hover:text-primary transition-colors text-left cursor-pointer"
                      >
                        {test.name}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-text">{test.code}</td>
                    <td className="px-4 py-3 text-sm text-text">{test.diagnosticCategory?.name || test.category}</td>
                    <td className="px-4 py-3 text-sm text-text">
                      {requiresSpecimen(test) ? (
                        <span className="inline-flex items-center gap-1.5">
                          {isBloodTest(test) && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">
                              <Droplet size={10} /> Blood
                            </span>
                          )}
                          <span>{testSpecimenTypeName(test) || testSpecimenTypeCode(test) || '—'}</span>
                        </span>
                      ) : (
                        <span className="text-text-muted text-xs">No sample (procedure)</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-text">৳{test.price?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-text">{test.discountPercent ? `${test.discountPercent}%` : '-'}</td>
                    <td className="px-4 py-3 text-sm text-text">{
                      test.room?.name ||
                      test.roomNumber ||
                      test.department?.rooms?.[0]?.name ||
                      '-'
                    }</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        test.status === 'ACTIVE' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                      }`}>
                        {test.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/administration/diagnostic-tests/${test.id}/edit`)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => setSubmitModal({ open: true, id: test.id, name: test.name })}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Submit Doc & Data"
                        >
                          <Send size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={limit}
          onPageChange={setPage}
          itemName="tests"
        />
      </div>

      {submitModal?.open && (
        <DiagnosticSubmitModal
          entityType="diagnostic_test"
          entityId={submitModal.id}
          entityName={submitModal.name}
          onClose={() => setSubmitModal(null)}
        />
      )}

      {/* View Modal */}
      {viewModal?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface z-10 flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <FlaskConical className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-text">Test Details</h2>
              </div>
              <button
                onClick={() => setViewModal(null)}
                className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {viewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : viewError || !test ? (
              <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger m-5">
                <AlertCircle size={18} />
                <span>Failed to load diagnostic test details. Please try again.</span>
              </div>
            ) : (
              <div className="p-5 space-y-5">
                {/* Profile Header Card */}
                <div className="bg-surface border border-border rounded-xl p-6">
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="flex-1 min-w-0 space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <FlaskConical size={20} className="text-primary" />
                            <h1 className="text-2xl font-bold text-text">{test.name}</h1>
                          </div>
                          <p className="text-sm text-text-muted mt-1 font-mono">{test.code}</p>
                        </div>
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium shrink-0 ${
                            test.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {test.status === 'ACTIVE' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                          {test.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Tag size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Category</p>
                            <p className="text-sm font-medium text-text">{test.diagnosticCategory?.name || test.category}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <DollarSign size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Price</p>
                            <p className="text-sm font-medium text-text">৳{test.price?.toLocaleString()}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Tag size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Discount</p>
                            <p className="text-sm font-medium text-text">{test.discountPercent ? `${test.discountPercent}%` : 'No discount'}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Clock size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Turnaround Time</p>
                            <p className="text-sm font-medium text-text">{test.turnaroundTime !== undefined && test.turnaroundTime !== null ? `${test.turnaroundTime} hour(s)` : 'N/A'}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <MapPin size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Room</p>
                            <p className="text-sm font-medium text-text">{test.room?.name || test.roomNumber || 'N/A'}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Droplet size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Sample Type</p>
                            <p className="text-sm font-medium text-text flex items-center gap-1.5">
                              {testSpecimenTypeName(test) || testSpecimenTypeCode(test) || 'N/A'}
                              {isBloodTest(test) && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">
                                  <Droplet size={10} /> Blood
                                </span>
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Tag size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Container</p>
                            <p className="text-sm font-medium text-text">{testContainerLabel(test)}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 bg-background rounded-lg p-3 border border-border">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <FlaskConical size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-xs text-text-muted">Specimen</p>
                            <p className="text-sm font-medium text-text">
                              {requiresSpecimen(test)
                                ? `Yes${requiredVolumeForTest(test) ? ` · ${requiredVolumeForTest(test)}` : ''}`
                                : 'No (procedure only)'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Preparation Instructions */}
                  <div className="bg-surface border border-border rounded-xl p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <List size={18} className="text-primary" />
                      <h2 className="text-lg font-semibold text-text">Preparation Instructions</h2>
                    </div>
                    <p className="text-sm text-text">{test.preparationInstructions || 'No preparation instructions provided.'}</p>
                  </div>

                  {/* Related Accessories */}
                  <div className="bg-surface border border-border rounded-xl p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <Tag size={18} className="text-primary" />
                      <h2 className="text-lg font-semibold text-text">Related Accessories</h2>
                    </div>
                    {(test.accessories || []).length === 0 ? (
                      <p className="text-sm text-text-muted">No accessories linked to this test.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {(test.accessories || []).map((a: any) => (
                          <span
                            key={a.id}
                            className="px-3 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20"
                          >
                            {a.accessory?.name || a.name} (৳{a.accessory?.price || a.price})
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Toggle */}
                <div className="bg-surface border border-border rounded-xl p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={18} className="text-primary" />
                    <h2 className="text-lg font-semibold text-text">Status Control</h2>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleToggleStatus}
                      disabled={togglingStatus || updateMutation.isPending}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        test.status === 'ACTIVE'
                          ? 'bg-success/10 text-success border border-success/20'
                          : 'bg-danger/10 text-danger border border-danger/20'
                      } disabled:opacity-50`}
                    >
                      {test.status === 'ACTIVE' ? <CheckCircle size={18} /> : <XCircle size={18} />}
                      {test.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                    </button>
                    <p className="text-xs text-text-muted">
                      {test.status === 'ACTIVE'
                        ? 'This test is available for diagnostic receipts.'
                        : 'This test is hidden from diagnostic receipts.'}
                    </p>
                  </div>
                </div>

                {/* Bottom Actions */}
                <div className="flex flex-wrap items-center justify-end gap-3 pt-4">
                  <button
                    onClick={() => {
                      setViewModal(null);
                      navigate(`/administration/diagnostic-tests/${test.id}/edit`);
                    }}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <Edit3 size={16} />
                    Edit Test
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selectedIds.length} test${selectedIds.length === 1 ? '' : 's'}?`}
        description="This will move the selected diagnostic tests to the trash. This cannot be undone here."
        confirmLabel={`Delete ${selectedIds.length} test${selectedIds.length === 1 ? '' : 's'}`}
        loading={bulkDeleteMutation.isPending}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
